import React from "react";

import { isEmpty } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { BusinessAction } from "actions";

import { DefaultVendorSplitAction } from "actions/vendorSplits";

import BusinessForm from "components/Business/BusinessForm";
import { BusinessFormValues } from "components/Business/BusinessForm/types";

import { closeAllHashModals, returnToLocation } from "lib/navigation";

import { getPropertyEnrichedBusinessByBusinessId } from "selectors";

interface EditBusinessComponentProps {
  businessId?: string;
  defaultTab?: string;
  returnTo?: string;
  defaultAddressSearch?: string;
}

const EditBusinessComponent = ({
  businessId,
  defaultTab,
  returnTo,
  defaultAddressSearch,
}: EditBusinessComponentProps): React.JSX.Element => {
  const dispatch = useDispatch();

  const editingBusiness = useSelector(
    getPropertyEnrichedBusinessByBusinessId(businessId),
  );
  const business = isEmpty(editingBusiness) ? undefined : editingBusiness;

  const closeSelf = React.useCallback(() => {
    if (returnTo) {
      returnToLocation(returnTo);
    } else {
      closeAllHashModals();
    }
  }, [returnTo]);

  // Ref to capture touched state from Form component
  const touchedRef = React.useRef<any>({});

  const handleSubmit = (submittedValues: BusinessFormValues): void => {
    // Do a little munging of the submitted values to satisfy some form logic.
    if (submittedValues.defaultVendorSplit?.splits?.length === 0) {
      if (submittedValues.defaultVendorSplit.id) {
        dispatch(
          DefaultVendorSplitAction.delete(
            submittedValues.defaultVendorSplit.id,
          ),
        );
      }
      delete submittedValues.defaultVendorSplit;
    }

    // if the primary contact email is not available in the
    // email recipients or business users anymore - remove it.
    if (submittedValues.primaryContactEmail) {
      const primaryContactIsEmailRecipient =
        submittedValues.emailRecipients.some(
          er => er.email === submittedValues.primaryContactEmail,
        );

      const primaryContactIsBusinessUser = submittedValues.businessUsers.some(
        bu => bu.email === submittedValues.primaryContactEmail,
      );

      if (!primaryContactIsEmailRecipient && !primaryContactIsBusinessUser) {
        submittedValues.primaryContactEmail = null;
      }
    }

    if (business) {
      // Only patch the fields that have been touched.
      // Do so pretty bluntly - eg if properties is touched, submit the lot.
      const touchedFields = Object.keys(touchedRef.current).filter(
        key => touchedRef.current[key],
      );
      const values: Record<string, any> = {
        id: businessId,
      };
      touchedFields.forEach(key => {
        values[key] = submittedValues[key];
      });

      if (
        submittedValues.generateBpayCRN &&
        values.bpayCustomerReferenceNumber
      ) {
        delete values.bpayCustomerReferenceNumber;
      }

      dispatch(
        BusinessAction.update(values, {
          changeReason: "Updated via edit business.",
        }),
      );
    } else {
      // But submit everything on a create.
      dispatch(
        BusinessAction.create({
          id: uuidv4(),
          ...submittedValues,
        }),
      );
    }
    closeSelf();
  };

  return (
    <BusinessForm
      business={business}
      defaultTab={defaultTab}
      handleSubmit={handleSubmit}
      closeSelf={closeSelf}
      defaultAddressSearch={defaultAddressSearch}
      touchedRef={touchedRef}
    />
  );
};

export const EditBusiness = React.memo(EditBusinessComponent);
